/**
 * Typed access to the Dispatch API through the gateway proxy.
 *
 * Request bodies are type-checked against the OpenAPI contract (src/api/generated/dispatch.d.ts,
 * regenerated with `npm run gen:types`). Dispatch does not document response bodies, so services
 * declare the response type they rely on (see src/domain/models).
 */
import type { components, paths } from './generated/dispatch'
import { request, requestRaw, type ApiSuccess } from './client'

export type Schemas = components['schemas']
type Paths = paths
type Method = 'get' | 'post' | 'put' | 'delete'

type PathsWith<M extends Method> = { [P in keyof Paths]: Paths[P] extends Record<M, { parameters: unknown }> ? P : never }[keyof Paths]

type BodyOf<P extends keyof Paths, M extends Method> = Paths[P][M & keyof Paths[P]] extends {
  requestBody?: { content: { 'application/json': infer B } }
}
  ? B
  : Paths[P][M & keyof Paths[P]] extends { requestBody?: { content: { 'multipart/form-data': unknown } } }
    ? FormData
    : undefined

type ParamsOf<P extends keyof Paths, M extends Method> = Paths[P][M & keyof Paths[P]] extends { parameters: { path: infer PP } }
  ? PP extends Record<string, unknown>
    ? { [K in keyof PP]: string | number }
    : undefined
  : undefined

export interface DispatchCallOptions<P extends keyof Paths, M extends Method> {
  params?: ParamsOf<P, M>
  body?: BodyOf<P, M> | FormData
  query?: Record<string, string | number | boolean | undefined>
  signal?: AbortSignal
  /** Headers the gateway reads and does not forward to Dispatch (e.g. `x-aos-assign-override`). */
  headers?: Record<string, string>
}

function buildPath(template: string, params?: Record<string, string | number>): string {
  const filled = template.replace(/\{([^}]+)\}/g, (_, key: string) => {
    const value = params?.[key]
    if (value === undefined || value === '') throw new Error(`Missing path parameter "${key}" for ${template}`)
    return encodeURIComponent(String(value))
  })
  return `/dispatch${filled.replace(/^\/api/, '')}`
}

const methodName = (m: Method) => m.toUpperCase() as 'GET' | 'POST' | 'PUT' | 'DELETE'

/** Calls a Dispatch endpoint and returns the full gateway envelope (data, data2, summary). */
export function dispatchCall<T, M extends Method, P extends PathsWith<M>>(method: M, path: P, opts: DispatchCallOptions<P, M> = {}): Promise<ApiSuccess<T>> {
  return request<T>(buildPath(path as string, opts.params as Record<string, string | number> | undefined), {
    method: methodName(method),
    body: method === 'get' ? undefined : (opts.body ?? {}),
    query: opts.query,
    signal: opts.signal,
    headers: opts.headers,
  })
}

/** Convenience: only the `data` payload. */
export async function dispatchData<T, M extends Method, P extends PathsWith<M>>(method: M, path: P, opts: DispatchCallOptions<P, M> = {}): Promise<T> {
  return (await dispatchCall<T, M, P>(method, path, opts)).data
}

/** Binary endpoints (PDF / Excel exports). Returns a Blob plus the server-suggested filename. */
export async function dispatchFile<M extends Method, P extends PathsWith<M>>(method: M, path: P, opts: DispatchCallOptions<P, M> = {}): Promise<{ blob: Blob; filename?: string; json?: unknown }> {
  const res = await requestRaw(buildPath(path as string, opts.params as Record<string, string | number> | undefined), {
    method: methodName(method),
    body: method === 'get' ? undefined : (opts.body ?? {}),
    signal: opts.signal,
  })
  const type = res.headers.get('content-type') ?? ''
  // Some exports answer JSON with a base64 / URL payload instead of a file.
  if (type.includes('json')) return { blob: new Blob(), json: await res.json() }
  const disposition = res.headers.get('content-disposition') ?? ''
  const filename = /filename="?([^";]+)/i.exec(disposition)?.[1]
  return { blob: await res.blob(), filename: filename ? decodeURIComponent(filename) : undefined }
}

/**
 * Untyped variant for configuration-driven screens (reports, master data). The path is still checked at
 * runtime by the gateway against the Swagger catalog and the user's permissions.
 */
export async function dispatchDynamic<T = unknown>(method: Method, template: string, opts: { params?: Record<string, string | number>; body?: unknown; signal?: AbortSignal } = {}): Promise<ApiSuccess<T>> {
  return request<T>(buildPath(template, opts.params), { method: methodName(method), body: method === 'get' ? undefined : (opts.body ?? {}), signal: opts.signal })
}
